/**
 * The pet on the battlefield (docs/PETS.md), the parts that need no GL: where it stands beside the rally flag (how it
 * gets there is petHop.ts / petGait.ts), and which reaction it plays — a bark when the fortress musters a troop (throttled), a cheer on
 * a cleared wave or the win, a hide when a boss arrives or a raider comes close, a fidget now and then. Pure (time in
 * seconds, randomness injected), so test/pets.test.ts pins it. Presentation only: the pet's power is in the sim.
 */

/** The pet rig's clips (the art contract) and their lengths, seconds: a stand-in with no clips still times its acts. */
export const PET_CLIP = { idle: 1.6, fidget: 2.0, walk: 0.5, run: 0.37, bark: 0.8, cheer: 1.33, hide: 1.2, sleep: 2.4 } as const;
/** When the two barks of the `bark` clip land (seconds in): the stand-in nods its head on them. */
export const BARK_PEAKS = [0.2, 0.5] as const;

export const PET_TUNING = {
  /** At most one bark this often (seconds), however fast the troops muster. */
  barkGap: 2.5,
  /** Seconds between fidgets while it stands idle (uniform in the range). */
  fidget: [6, 14] as const,
  /** A raider this near the pet (cells, centre to centre) sends it into hiding. */
  threatReach: 0.8,
  /** Where it sits from the flag's cell: this far behind the formation (cells, against its facing)… */
  behind: 0.62,
  /** …and this far to the side, so it never hides the troops or the flag; level with them, this far out past a flank. */
  side: 0.42,
  flank: 0.68,
  /** The spot stays this near its cell's centre (cells). */
  inCell: 0.3,
  /** With no flag out yet (the rally on the fortress itself): this far out in front of the gate instead. */
  keepFront: 0.95,
  /** How long a reaction asked for while it cannot play (mid-hop, mid-cheer) stays wanted, seconds. */
  keep: { bark: 0.3, fidget: 0, cheer: 1.5, hide: 0.5 },
} as const;

/** A reaction one-shot. */
export type PetAct = "bark" | "cheer" | "hide" | "fidget";
/** A higher act cuts a lower one; an act never cuts one of its own rank or above (a hide may follow a hide). */
const RANK: Readonly<Record<PetAct, number>> = { fidget: 0, bark: 1, cheer: 2, hide: 3 };

/** The reaction state: what plays, what is wanted next, when the next bark and fidget may come. */
export class PetMind {
  private act: PetAct | "" = "";
  private actEnd = 0;
  private lastBark = -Infinity;
  private fidgetAt: number;
  private readonly wanted = new Map<PetAct, number>();

  constructor(private readonly rand: () => number = Math.random, now = 0) { this.fidgetAt = now + this.fidgetGap(); }

  /** The one-shot playing at `now`, "" when none. */
  playing(now: number): PetAct | "" { return this.act !== "" && now < this.actEnd ? this.act : ""; }

  /** Something happened that this act answers (it plays as soon as it may, within PET_TUNING.keep). */
  ask(act: PetAct, now: number): void {
    if (act === "bark" && now - this.lastBark < PET_TUNING.barkGap) return;
    this.wanted.set(act, now + PET_TUNING.keep[act]);
  }

  /**
   * The act to start now, or null: the highest one wanted that may play — never mid-hop (a hide waits for the
   * landing), never over an act of its rank or above, a fidget only when idle and due. The caller plays it and
   * reports its length (`started`).
   */
  next(now: number, moving: boolean): PetAct | null {
    for (const [a, until] of this.wanted) if (until < now) this.wanted.delete(a);
    if (moving) { if (this.playing(now) === "fidget") this.act = ""; return null; }
    const cur = this.playing(now);
    let best: PetAct | null = null;
    for (const a of this.wanted.keys()) if (best === null || RANK[a] > RANK[best]) best = a;
    if (best !== null && (cur === "" || RANK[best] > RANK[cur])) return best;
    if (cur === "" && now >= this.fidgetAt) return "fidget";
    return null;
  }

  /** `act` started at `now`; `len` is its clip's length (0 for a stand-in: the contract's length is used). */
  started(act: PetAct, len: number, now: number): void {
    this.act = act;
    this.actEnd = now + (len > 0 ? len : PET_CLIP[act]);
    this.wanted.delete(act);
    if (act === "bark") this.lastBark = now;
    this.fidgetAt = this.actEnd + this.fidgetGap();
  }

  private fidgetGap(): number { const [a, b] = PET_TUNING.fidget; return a + this.rand() * (b - a); }
}

/**
 * The spots the pet tries round a formation, in order, as (along the facing, to its right) in cells: behind the ranks
 * to either side, then level with them just past their flanks, then ahead of them — the first one on a cell it may
 * stand on wins (the default rally sits right outside the gate, where "behind" is the fortress wall).
 */
const SPOTS: readonly (readonly [number, number])[] = [
  [-PET_TUNING.behind, PET_TUNING.side], [-PET_TUNING.behind, -PET_TUNING.side],
  [0, PET_TUNING.flank], [0, -PET_TUNING.flank],
  [PET_TUNING.behind, PET_TUNING.side], [PET_TUNING.behind, -PET_TUNING.side],
];
/** With no flag out yet (the rally on the fortress itself): out in front of the gate, a little to either side. */
const KEEP_SPOTS: readonly (readonly [number, number])[] = [[PET_TUNING.keepFront, PET_TUNING.side], [PET_TUNING.keepFront, -PET_TUNING.side]];

/**
 * Where the pet sits for a formation at `rally` (cell x, y) facing `[fx, fy]` (headingOffset), world x / z: the first of
 * the spots whose cell is `standable` and `clear` (not hard by the fortress, whose walls overhang its cells and hide
 * whatever stands behind them), else the first standable one, else the first spot. Cell centres are whole numbers.
 * `reach` stretches the spots out for a pet drawn larger than life (petPresent.ts spotReach).
 */
export function petSpot(rally: { readonly x: number; readonly y: number }, facing: readonly [number, number], atKeep: boolean, out: { x: number; z: number },
  standable: (cx: number, cy: number) => boolean = () => true, clear: (cx: number, cy: number) => boolean = () => true, reach = 1): { x: number; z: number } {
  const [fx, fy] = facing;
  const spots = atKeep ? KEEP_SPOTS : SPOTS;
  const place = ([along, right]: readonly [number, number]) => {
    // right of the facing (fx, fy) is (fy, −fx) in cell space
    out.x = rally.x + (fx * along + fy * right) * reach;
    out.z = rally.y + (fy * along - fx * right) * reach;
    return [Math.round(out.x), Math.round(out.z)] as const;
  };
  const pick = spots.find((s) => { const [cx, cz] = place(s); return standable(cx, cz) && clear(cx, cz); })
    ?? spots.find((s) => { const [cx, cz] = place(s); return standable(cx, cz); }) ?? spots[0];
  place(pick);
  // well inside its cell: never out on the island's rim or half on the next cell (a tower's, a cliff's)
  const cx = Math.round(out.x), cz = Math.round(out.z), k = PET_TUNING.inCell;
  out.x = Math.min(cx + k, Math.max(cx - k, out.x));
  out.z = Math.min(cz + k, Math.max(cz - k, out.z));
  return out;
}

/** How near (cells: the next cell, diagonals too) and how squarely (cosine of the angle) a fortress cell must stand toward the camera to hide the pet. */
const KEEP_SHADE = { reach: 1.5, cos: 0.6 } as const;

/**
 * Whether one of `keep` (the fortress's cells) stands between cell (cx, cy) and the camera — `toCamera` is the unit
 * ground direction from the scene toward it (x, z) — near enough for its walls and roofs to hide a pet there. Pure.
 */
export function keepHides(cx: number, cy: number, keep: readonly { readonly x: number; readonly y: number }[], toCamera: { readonly x: number; readonly z: number }): boolean {
  for (const k of keep) {
    const dx = k.x - cx, dz = k.y - cy, d = Math.hypot(dx, dz);
    if (d === 0) return true;
    if (d <= KEEP_SHADE.reach && (dx * toCamera.x + dz * toCamera.z) / d >= KEEP_SHADE.cos) return true;
  }
  return false;
}

/**
 * How a tower hides what stands behind it from the play camera (35° up): anything on the ground up to `reach` cells
 * beyond it along the view and within `half` a cell of its line of sight (a tower ~1.8 tall at that tilt covers about
 * 2.5 cells of ground behind its centre, and its roof's far edge half a cell more; a roof's width across).
 */
const TOWER_SHADE = { reach: 3, half: 0.6 } as const;

/**
 * Whether one of `towers` (cells) stands between cell (cx, cy) and the camera — `toCamera` the unit ground direction
 * from the scene toward it — near and square enough to hide a pet there. Pure.
 */
export function towerHides(cx: number, cy: number, towers: readonly { readonly x: number; readonly y: number }[], toCamera: { readonly x: number; readonly z: number }): boolean {
  for (const t of towers) {
    const dx = t.x - cx, dz = t.y - cy;
    if (dx === 0 && dz === 0) return true;
    const along = dx * toCamera.x + dz * toCamera.z, across = Math.abs(dx * toCamera.z - dz * toCamera.x);
    if (along > 0 && along <= TOWER_SHADE.reach && across <= TOWER_SHADE.half) return true;
  }
  return false;
}

/** The yaw (about y) that looks along `[fx, fy]`, as the troops face the road. */
export function faceYaw(facing: readonly [number, number]): number { return Math.atan2(facing[0], facing[1]); }

/** Whether a raider at (ex, ez) is within the threat reach of the pet at (x, z). */
export function withinReach(x: number, z: number, ex: number, ez: number, reach: number = PET_TUNING.threatReach): boolean {
  const dx = ex - x, dz = ez - z;
  return dx * dx + dz * dz <= reach * reach;
}

/** What the stand-in (no model shipped yet) shows for an act: a lift, a squash, a head nod and turn, a tail wag. */
export interface StandInPose { lift: number; squash: number; nod: number; look: number; wag: number }

/** How the stand-in moves: lifts and squashes in world units / scale shares, the head and tail in radians. */
const STAND_IN = { cheerLift: 0.07, cheerHops: 2, hideSquash: 0.4, sleepSquash: 0.3, barkNod: 0.45, barkWidth: 0.09, look: 0.6, wag: 0.5, wagHz: 3, breath: 0.025, breathHz: 0.9 } as const;

/** The stand-in's pose `t` seconds into `act` ("" idle, "walk", "sleep"). Pure. */
export function standInPose(act: PetAct | "sleep" | "walk" | "", t: number, out: StandInPose = { lift: 0, squash: 0, nod: 0, look: 0, wag: 0 }): StandInPose {
  out.lift = 0; out.nod = 0; out.look = 0;
  out.squash = STAND_IN.breath * Math.sin(t * Math.PI * 2 * STAND_IN.breathHz);
  out.wag = STAND_IN.wag * Math.sin(t * Math.PI * 2 * STAND_IN.wagHz);
  const u = (len: number) => Math.min(1, Math.max(0, t / len));
  switch (act) {
    case "bark": for (const p of BARK_PEAKS) out.nod += STAND_IN.barkNod * Math.max(0, 1 - Math.abs(t - p) / STAND_IN.barkWidth); break;
    case "cheer": out.lift = STAND_IN.cheerLift * Math.abs(Math.sin(u(PET_CLIP.cheer) * Math.PI * STAND_IN.cheerHops)); out.wag *= 2; break;
    case "hide": out.squash = -STAND_IN.hideSquash * Math.sin(u(PET_CLIP.hide) * Math.PI); out.wag = 0; break;
    case "fidget": out.look = STAND_IN.look * Math.sin(u(PET_CLIP.fidget) * Math.PI * 2); break;
    case "sleep": out.squash = -STAND_IN.sleepSquash + out.squash; out.wag = 0; out.nod = STAND_IN.barkNod; break;
    default: break;
  }
  return out;
}
